import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { edgeErrorMessage } from "@/lib/edge-errors";

export interface ChatSummary {
  points: string[];
  forYou: string[];
  count: number;
}

type SummaryResponse = Partial<ChatSummary> & { error?: string; skipped?: string; model?: string };

/** Meldinger når edge function-en svarer `skipped` i stedet for en oppsummering. */
export const SUMMARY_SKIPPED_TEXT: Record<string, string> = {
  flag_off: "AI-oppsummering er ikke slått på for deg ennå.",
  disabled: "AI-oppsummering er slått av i dette miljøet.",
  no_messages: "Det er ingen uleste meldinger fra andre å oppsummere.",
};

/**
 * AI-oppsummering av uleste meldinger i én kanal (VIB-3027). Edge function
 * `summarize-chat` henter meldingene selv fra `since` (første uleste da
 * kanalen ble åpnet), sjekker tilgang og logger forbruket. Lagrer ingenting
 * i klienten, så ingen cache å invalidere.
 */
export function useChatSummary() {
  return useMutation({
    mutationFn: async (input: { channelKey: string; since: string }): Promise<ChatSummary> => {
      const { data, error } = await supabase.functions.invoke<SummaryResponse>("summarize-chat", {
        body: { channel: input.channelKey, since: input.since },
      });
      if (error) throw new Error(await edgeErrorMessage(error, data, "Kunne ikke oppsummere"));
      if (data?.skipped) throw new Error(SUMMARY_SKIPPED_TEXT[data.skipped] ?? "Oppsummering er ikke tilgjengelig");
      if (!data?.points?.length) throw new Error("Kunne ikke oppsummere");
      return { points: data.points, forYou: data.forYou ?? [], count: data.count ?? 0 };
    },
  });
}
